import { Table, Typography, message, Tag, Button, Popconfirm, Space, Alert, Input, Tooltip } from 'antd';
import { useRecoilValue } from 'recoil';
import { themeState } from '../../atom';
import CustomCard from '../../components/Card';
import { useEffect, useRef, useState } from 'react';
import ApiService from '../../APIServices/ApiService';
import { ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

/**
 * Account deletion requests, and how long each has been waiting.
 *
 * WHY THIS PAGE EXISTS. The website has had a public "delete my account" form
 * for a long time, writing `AccountDeletionRequest` rows — and nothing ever
 * read them. No admin route served them and no page listed them, so every
 * request anybody submitted went into a collection no human could see. An
 * erasure request carries a statutory deadline, so invisible is the one thing
 * it could not be.
 *
 * TWO KINDS OF ROW, AND THE DIFFERENCE MATTERS. A `web` request is a name, an
 * email and a reason typed into a public form with nothing to prove the person
 * owns that address — anyone can file one against anyone. An `app` request was
 * raised from a signed-in session, so the account is the caller's own. Acting
 * on a `web` row should mean verifying the owner first; the column says which
 * you are looking at rather than leaving them to look identical.
 *
 * MARKING ONE DONE DOES NOT DELETE ANYTHING. Erasing an account touches
 * orders, entitlements, eSIM lines and wallet credit, and doing that from a
 * list-page button is how a paying customer's history disappears by accident.
 * This records that a person has dealt with it.
 */
const AccountDeletions = () => {
  const theme = useRecoilValue(themeState);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Held in a ref: the note is typed inside a Popconfirm that unmounts on
  // confirm, so state set from it would be gone before it is read.
  const noteRef = useRef('');

  const fetchRows = async (all = showAll) => {
    try {
      setLoading(true);
      const data = await ApiService.getAccountDeletionRequests(all ? 'all' : 'pending');
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error('Could not load deletion requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  /**
   * Approve or reject. Records the DECISION — it deletes nothing itself.
   *
   * Two answers because there really are two: a request filed through the
   * public web form carries no proof the person owns that address, and one
   * against an account holding unspent credit or a live eSIM is usually
   * somebody who wants a refund rather than erasure. Recording only "done"
   * left those looking identical to the ones that were honoured.
   */
  const decide = async (id, decision, note) => {
    try {
      await ApiService.decideAccountDeletionRequest(id, decision, note);
      message.success(decision === 'approve' ? 'Approved' : 'Rejected');
      fetchRows();
    } catch (error) {
      // A row somebody else already decided answers 409 rather than being
      // overwritten — say so instead of a generic failure.
      const already = error?.response?.data?.status;
      message.error(
        already
          ? `This request was already ${already}.`
          : 'Could not update that request',
      );
      fetchRows();
    }
  };

  // Anything older than this has very likely blown its statutory deadline.
  const overdue = rows.filter((r) => r.status === 'pending' && r.waitingDays > 25).length;

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'email',
      key: 'email',
      render: (email, row) => (
        <div>
          <div><Text strong>{row.name}</Text></div>
          <Text type="secondary" style={{ fontSize: 12 }}>{email}</Text>
        </div>
      ),
    },
    {
      title: 'Asked from',
      dataIndex: 'source',
      key: 'source',
      render: (source) =>
        source === 'app' ? (
          <Tag color="green">App — signed in</Tag>
        ) : (
          <Tag color="orange">Web form — unverified</Tag>
        ),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Waiting',
      dataIndex: 'waitingDays',
      key: 'waitingDays',
      sorter: (a, b) => a.waitingDays - b.waitingDays,
      defaultSortOrder: 'descend',
      render: (days, row) =>
        row.status !== 'pending' ? (
          <Text type="secondary">—</Text>
        ) : (
          <Text type={days > 25 ? 'danger' : days > 14 ? 'warning' : undefined}>
            {days} {days === 1 ? 'day' : 'days'}
          </Text>
        ),
    },
    {
      title: 'Requested',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      render: (d) => (d ? moment(d).format('D MMM YYYY, HH:mm') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, row) =>
        status === 'completed' ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Approved</Tag>
        ) : status === 'rejected' ? (
          <Tooltip title={row.note || undefined}>
            <Tag color="default">Rejected</Tag>
          </Tooltip>
        ) : (
          <Tag color="processing">Pending</Tag>
        ),
    },
    {
      title: '',
      key: 'action',
      render: (_, row) =>
        row.status === 'pending' ? (
          <Space>
            <Popconfirm
              title="Approve this deletion?"
              description="Delete the account first, then approve. Approving records the decision; it does not delete anything by itself."
              okText="Approve"
              okButtonProps={{ danger: true }}
              onConfirm={() => decide(row.id, 'approve')}
            >
              <Button size="small" danger>Approve</Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this request?"
              description={
                <div style={{ maxWidth: 280 }}>
                  Use this when the request is not genuine, or the customer
                  wants something else — a refund, or help with an eSIM.
                  <Input.TextArea
                    rows={2}
                    placeholder="Reason (optional, kept on the row)"
                    style={{ marginTop: 8 }}
                    onChange={(e) => { noteRef.current = e.target.value; }}
                  />
                </div>
              }
              okText="Reject"
              onConfirm={() => {
                decide(row.id, 'reject', noteRef.current);
                noteRef.current = '';
              }}
            >
              <Button size="small">Reject</Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <CustomCard>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0, color: theme === 'dark' ? '#fff' : undefined }}>
            Account deletion requests
          </Title>
          <Button onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show pending only' : 'Show all'}
          </Button>
        </div>

        {overdue > 0 && (
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={`${overdue} request${overdue === 1 ? ' has' : 's have'} been waiting more than 25 days`}
            description="An erasure request must normally be answered within one month of being received."
          />
        )}

        <Alert
          type="info"
          showIcon
          message="Approving a request does not delete the account"
          description="Delete the user first, then mark the row. Requests from the public web form are not proof of ownership — verify the customer before acting on one."
        />

        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: showAll ? 'No requests' : 'Nothing waiting' }}
        />
      </Space>
    </CustomCard>
  );
};

export default AccountDeletions;
